/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor(container) {
    this.container = container;
    this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    // Both are left out when predictive search is turned off in the theme
    // settings: the drawer is then a plain search form.
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
    this.status = this.container.querySelector('[data-predictive-search-status]');
    this.cache = new Map();
    // Whether a response has replaced the drawer's initial spinner yet.
    this.hasRendered = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.button.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        if (OverlayManager.isOpen(this.container)) {
          OverlayManager.close(this.container);
        } else {
          this.stampPromotionTemplate();
          OverlayManager.open(this.container, { opener: item });
          setTimeout(() => {
            this.input.focus({
              preventScroll: true
            });
          }, 100);
          dispatchCustomEvent('search:open');
        }
      });
    });

    if (!this.predictiveSearchResults) {
      return;
    }

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));

    this.predictiveSearchResults.addEventListener('click', (event) => {
      const submitButton = event.target.closest('#search-results-submit');
      if (submitButton) {
        this.form.submit();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      // A response still on its way would otherwise announce itself.
      themeFetch.abort('predictive-search');
      this.setLoading(false);
      this.predictiveSearchResults.classList.remove('active');
      this.announce('');
      return;
    }
    this.predictiveSearchResults.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  getSearchResults(searchTerm) {
    // Serve from cache if available
    if (this.cache.has(searchTerm)) {
      themeFetch.abort('predictive-search');
      this.setLoading(false);
      this.renderSearchResults(this.cache.get(searchTerm));
      return;
    }

    this.setLoading(true);
    if (!this.hasRendered) {
      this.announce(window.theme?.strings?.loading || 'Loading');
    }

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '10',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
      section_id: 'predictive-search'
    });

    themeFetch(`${theme.routes.predictive_search_url}?${params.toString()}`, {}, 'predictive-search')
      .then((response) => response.text())
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Cap cache size
        if (this.cache.size >= 50) {
          const firstKey = this.cache.keys().next().value;
          this.cache.delete(firstKey);
        }
        this.cache.set(searchTerm, resultsMarkup);

        this.setLoading(false);
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }
        // Back to the drawer's initial panel (popular searches).
        this.setLoading(false);
        this.predictiveSearchResults.classList.remove('active');
        this.announce('');
      });
  }

  setLoading(loading) {
    this.predictiveSearchResults.classList.toggle('loading', loading);
    this.predictiveSearchResults.setAttribute('aria-busy', loading);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.hasRendered = true;

    const count = this.predictiveSearchResults.querySelector('[data-predictive-search-count]');
    this.announce(count ? count.textContent.trim() : '');
  }

  // Polite: read out once the shopper pauses, without moving focus.
  announce(message) {
    if (this.status && this.status.textContent !== message) {
      this.status.textContent = message;
    }
  }

  stampPromotionTemplate() {
    const tpl = this.container.querySelector('.thb-predictive-search--promotion-template');
    if (tpl) {
      tpl.replaceWith(tpl.content.cloneNode(true));
    }
  }
}
window.addEventListener('load', () => {
  const container = document.getElementById('Search-Drawer');
  if (container && typeof PredictiveSearch !== 'undefined') {
    new PredictiveSearch(container);
  }
});
